/**
 * @Copyright 2014-2018 DataVoke, Inc. All rights reserved.
 */

'use strict';

import {
    deepClone,
    Dictionary,
    noop
} from "../utils/tools";
import { ClientProperties } from "../entities/entitybase";
import {
    EntityStates,
    EntityTypes
} from "../utils/dvtypes";

export const allPendingChanges = new Dictionary();


/**
 * Return an array of all pending change objects for the currently logged in user.
 * @returns {Array}
 */
export function getPendingChangesForThisUser () {
    return allPendingChanges.Values.where(change => change.UserID === app.dv.cache.session.currentUser.User.UserID);
}

/**
 *
 * @param {EntityTypes} entityType - The type of entity the pending change is being searched for.
 * @param {number} entityID - The ID of the entity being searched for.
 * @returns {null|PendingChange} - The PendingChange entity that matches the search criteria.
 */
export function getPendingChangesByEntityTypeAndID (entityType, entityID) {
    return allPendingChanges.Values.where(change => change.clientProperties.changedEntityType === entityType && change.EntityID === entityID);
}

/**
 * Get the specified pending change entity from the service as specified by the ID.
 * @param {number} pendingChangeID - The ID of the pending change entity to pull down.
 * @param {function} [callback = noop] - OPTIONAL callback function if not being used as a promise.
 * @returns {Promise}
 */
export function getSvcPendingChangeByID(pendingChangeID, callback = noop) {
    return new Promise((resolve, reject) => {
        let reqData = {
            Session: app.dv.cache.session.sessionGUID,
            PendingChangeID: pendingChangeID
        };

        app.dv.services.call(app.dv.services.endpoints.getPendingChangeByID, reqData, (results) => {
            if (results != null) {
                app.dv.cache.loadPendingChangeCache([results]);
            }
            callback(results);
            resolve(results);
        });
    });
}

/**
 * Commit the specified pending change(s).
 * @param {Array|PendingChange} pendingChanges - Single or array of PendingChange objects to commit.
 * @param {function} [callback = noop] - OPTIONAL callback function for the results.
 * @returns {Promise}
 */
export function commitPendingChanges (pendingChanges, callback = noop) {
    if (pendingChanges == null) {
        callback(true);
        return Promise.resolve(true);
    }

    let CA = app.dv.cache;

    if (!Array.isArray(pendingChanges)) {
        pendingChanges = [pendingChanges];
    }

    let reqData = {
        Session: app.dv.cache.session.sessionGUID,
        ChangeEntityIDs: pendingChanges.select(pendingChange => pendingChange.PendingChangeID)
    };

    return app.dv.services.call(app.dv.services.endpoints.commitPendingChanges, reqData).then((data) => {
        //This will happen if there is no result or the length is 0.  (trying to save nothing (security groups) can cause this)...BC
        if (data == null || data.length === 0) {
            callback(true);
            return true;
        }

        pendingChanges.forEach((currResult) => {
            // update the cache for each pending change object.
            switch (currResult.TableName) {
                case "View":
                    CA.views.allOriginalViews.remove(currResult.EntityID);
                    break;

                default:
                    console.warn(`Commit performed on unsupported pending change entity type ${currResult.TableName}`);
            }

            // remove from the cache.
            CA.pendingChanges.allPendingChanges.remove(currResult.PendingChangeID);

            //Update the rail notifications...eg
            app.designer.rails.settingsRail.updateConflictNotifications();
        });

        return true;
    });
}

/**
 * Revert the specified pending change(s).
 * @param {Array|PendingChange} pendingChanges - Single or array of PendingChange objects to revert.
 * @param {function} [callback = noop] - OPTIONAL callback function for the results.
 */
export function revertPendingChanges (pendingChanges, callback = noop) {
    if (pendingChanges == null) {
        callback(true);
        return Promise.resolve(true);
    }

    let CA = app.dv.cache;

    let entities = [];

    if (!Array.isArray(pendingChanges)) {
        pendingChanges = [pendingChanges];
    }

    pendingChanges.forEach((pendingChange) => {
        // clone the change so we can strip off unnecessary data
        let change = deepClone(pendingChange, "clientProperties");

        // since we are deleting the entity, we don't need to send up the Definition or Metadata
        change.Definition = undefined;
        change.Metadata = undefined;
        entities.push(change);

        // all of these are actually going to "delete" as they are "saved".
        change._State = pendingChange._State = EntityStates.Deleted;
    });

    let reqData = {
        Session: app.dv.cache.session.sessionGUID,
        PendingChangeID: entities
    };

    return app.designer.saveEntityToServer(entities, "PendingChangeID", app.dv.services.endpoints.savePendingChanges, CA.pendingChanges.allPendingChanges).then((success) => {
        if (success) {
            entities.forEach((change) => {
                switch (change.TableName) {
                    case "View":
                        //Restore the view from the our local repository...EK
                        let originalView = CA.views.allOriginalViews.getVal(change.EntityID);
                        if (originalView != null) {
                            CA.views.allViews.add(originalView.ViewID, originalView);
                            CA.views.allOriginalViews.remove(originalView.ViewID);
                        }
                        break;

                    default:
                        console.warn(`Unsupported pending change type ${change.TableName} was "reverted".`);
                }

                // remove the deleted pending change from cache.
                CA.pendingChanges.allPendingChanges.remove(change.PendingChangeID);

                //Update the rail notifications...eg
                app.designer.rails.settingsRail.updateConflictNotifications();
            });

            callback(true);
            return true;
        }
        else {
            console.error("Unable to revert one or more pending changes.");
            callback(false);
            return false;
        }
    });
}

/**
 *
 * @param {PendingChange} entity - The pending change entity to add entity-specific client properties to.
 */
export function applyClientProperties (entity) {
    if (entity == null) { return; }
    entity.clientProperties = new ClientProperties(EntityTypes.PendingChange);
    switch (entity.TableName) {
        case "View":
            entity.clientProperties.changedEntityType = EntityTypes.View;
            break;

        default:
            console.warn(`Unsupported pending change target entity type ${entity.TableName} for Pending Change ID ${entity.PendingChangeID}`);
    }

}
